/**
 * Mission agents (ADR-022): deterministic characters with READABLE CUES and
 * REAL DESTINATIONS. Vehicles follow lanes along their own waypoint paths
 * (they have somewhere to go), brake smoothly for the robot instead of
 * swerving across it, and may overtake on the left when there is room.
 * People walk, stand and talk, or cross — and only alleys allow crossing.
 *
 * Every mover telegraphs through cuePhase before it can matter, and every
 * mover carries a courtesy guard — it halts rather than touch the robot
 * (the player can still hit them). All randomness comes from seeded RNG
 * streams fixed at construction; all positions are world coordinates that
 * the mission builder mirrors as a whole.
 */

import { Rng, deriveSeed } from "../core/rng";
import { clamp, smoothstep01, wrapAngle, type Vec2 } from "../core/vec";
import {
  DRIFT_RAMP_S,
  DRIFT_TARGET,
  PED_REACTIVE_SLOW_SPEED,
  PED_REACTIVE_TTC,
  REACTIVE_CLEARANCE,
  SAFETY_MARGIN,
  SWAY_AMPLITUDE,
  SWAY_HZ,
} from "./constants";
import type { RoadNetwork } from "./network";
import type { DeliveryPath, PathFrame } from "./path";
import { timeToCollision } from "./safety";
import type { Obstacle, RobotState, Side } from "./types";
import { bodyCircles, type RobotShape } from "./world";

export type AgentType =
  | "research_ped"
  | "standing_person"
  | "walker"
  | "cat"
  | "dog"
  | "motorbike"
  | "motorcycle"
  | "car"
  | "truck"
  | "bus"
  | "vendor_cart";

export interface AgentState {
  id: string;
  type: AgentType;
  /** Free-text role for the renderer/validation ("group", "oncoming", "pullout", ...). */
  role: string;
  x: number;
  z: number;
  vx: number;
  vz: number;
  /** Sway-free intent velocity for the robot's safety TTC. */
  safetyVx: number;
  safetyVz: number;
  /** Collision circle radius (m) of each body circle. */
  radius: number;
  /** Axial offsets (m, +forward along heading) of the body circles. */
  body: readonly number[];
  /** Facing (rad from +Z). */
  heading: number;
  active: boolean;
  collidable: boolean;
  /** 0 = idle; increasing integers = readable cue steps (renderer maps them). */
  cuePhase: number;
  moving: boolean;
}

export interface MissionWorld {
  network: RoadNetwork;
  path: DeliveryPath;
}

export interface AgentCtx {
  t: number;
  dt: number;
  robot: Readonly<RobotState>;
  robotShape: RobotShape;
  world: MissionWorld;
  /** The robot's path frame this step. */
  robotFrame: PathFrame;
  /** The per-encounter decision for THIS agent's encounter, once made. */
  decision: Side | "yield" | null;
  /** Research pedestrian only: scripted-window bookkeeping. */
  cueT: number | null;
  reactive: boolean;
  /** Static props, for go-around side probes (ADR-066). */
  obstacles?: readonly Obstacle[];
  /** Every agent's state (self included), for go-around side probes. */
  others?: readonly AgentState[];
}

export interface AgentModel {
  readonly state: AgentState;
  step(ctx: AgentCtx): void;
  /** True exactly once per courtesy-guard hard halt (abrupt-movement metric). */
  consumeAbruptStop(): boolean;
  /** Research pedestrian only. */
  driftSide?(): Side | null;
  /** Path progress at which this agent starts acting (null = from the start). */
  readonly triggerS: number | null;
}

/** World-space body circles of an agent (center + axial offsets). */
export function agentCircles(s: AgentState): Vec2[] {
  const fx = Math.sin(s.heading);
  const fz = Math.cos(s.heading);
  return s.body.map((o) => ({ x: s.x + fx * o, z: s.z + fz * o }));
}

// ---------------------------------------------------------------------------

const GUARD_MARGIN = 0.15;
/** After this long halted by the guard beside a STATIONARY robot, an agent
 * may creep past at walking pace (contact-free): breaks mutual deadlocks
 * between the agent's courtesy guard and the robot's safety governor. */
const CREEP_AFTER_S = 1.5;
const CREEP_SPEED = 0.6;
const CONTACT_MARGIN = 0.06;
/** The robot counts as standing for creep purposes below this speed (m/s):
 * governor-induced twitches must not cancel a creep in progress. */
const CREEP_ROBOT_STILL = 1.0;
/** A creeping body keeps at least this beyond the robot's safety margin
 * (r + R + SAFETY_MARGIN) unless it is already closer and moving apart, so
 * the robot's TTC channel never sees it as a collision course. */
const CREEP_CLEAR = 0.2;

function baseState(
  id: string,
  type: AgentType,
  role: string,
  x: number,
  z: number,
  radius: number,
  heading: number,
  body: readonly number[] = [0],
): AgentState {
  return {
    id,
    type,
    role,
    x,
    z,
    vx: 0,
    vz: 0,
    safetyVx: 0,
    safetyVz: 0,
    radius,
    body,
    heading,
    active: true,
    collidable: true,
    cuePhase: 0,
    moving: false,
  };
}

abstract class BaseAgent implements AgentModel {
  readonly state: AgentState;
  readonly triggerS: number | null;
  protected abrupt = false;
  protected prevSpeed = 0;
  /** Seconds spent halted by the courtesy guard (reset when it moves). */
  protected haltedS = 0;
  /** Last abrupt-stop timestamp (one per 2 s per agent; a flickering guard is one event). */
  private lastAbruptT = -Infinity;
  protected nowT = 0;
  /** Committed back-off window (a wedged body rolls back ~0.4 m, not 1 cm). */
  private reverseUntil = -Infinity;
  /** Whether guard halts count toward the "forced abrupt movement" metric. */
  protected abruptEligible = true;

  constructor(state: AgentState, triggerS: number | null) {
    this.state = state;
    this.triggerS = triggerS;
  }

  abstract step(ctx: AgentCtx): void;

  consumeAbruptStop(): boolean {
    const a = this.abrupt;
    this.abrupt = false;
    return a;
  }

  /** Smallest center distance at which any body circle would touch the robot. */
  protected guardRadius(ctx: AgentCtx): number {
    return this.state.radius + ctx.robotShape.effR + GUARD_MARGIN;
  }

  /** True when moving the body to (nx, nz) would crowd the robot. */
  protected wouldCrowd(ctx: AgentCtx, nx: number, nz: number, heading: number): boolean {
    const g = this.guardRadius(ctx);
    const fx = Math.sin(heading);
    const fz = Math.cos(heading);
    for (const o of this.state.body) {
      if (Math.hypot(nx + fx * o - ctx.robot.x, nz + fz * o - ctx.robot.z) < g) return true;
    }
    return false;
  }

  /** Smallest distance between this body (at nx, nz, heading) and the robot's body circles. */
  private bodyGap(ctx: AgentCtx, nx: number, nz: number, heading: number): number {
    const fx = Math.sin(heading);
    const fz = Math.cos(heading);
    let best = Infinity;
    for (const o of this.state.body) {
      for (const c of bodyCircles(ctx.robot, ctx.robotShape)) {
        best = Math.min(best, Math.hypot(nx + fx * o - c.x, nz + fz * o - c.z));
      }
    }
    return best;
  }

  /**
   * True when moving the body to (nx, nz) would touch the robot: inside the
   * contact margin and not moving apart (a move that increases separation is
   * allowed even from inside the margin, so a body can always back out), or
   * within the physical radii regardless.
   */
  protected wouldTouch(ctx: AgentCtx, nx: number, nz: number, heading: number): boolean {
    const s = this.state;
    const physical = s.radius + ctx.robotShape.r;
    const lim = physical + SAFETY_MARGIN + CREEP_CLEAR;
    const after = this.bodyGap(ctx, nx, nz, heading);
    if (after < physical + CONTACT_MARGIN) return true;
    if (after >= lim) return false;
    // Inside the clearance band: moving apart or (mostly) tangentially past
    // the robot is fine; closing on it is not.
    const before = this.bodyGap(ctx, s.x, s.z, s.heading);
    const step = Math.hypot(nx - s.x, nz - s.z);
    return after < before - 0.5 * step;
  }

  /**
   * Move toward (tx, tz) at `speed`, halting if the move would bring the body
   * within guard distance of the robot. Returns true when it actually moved.
   */
  protected guardedMove(ctx: AgentCtx, tx: number, tz: number, speed: number): boolean {
    const s = this.state;
    this.nowT = ctx.t;
    const dx = tx - s.x;
    const dz = tz - s.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 1e-6 || speed <= 0) {
      this.setVel(0, 0);
      return false;
    }
    let step = Math.min(dist, speed * ctx.dt);
    let nx = s.x + (dx / dist) * step;
    let nz = s.z + (dz / dist) * step;
    const heading = Math.atan2(dx / dist, dz / dist);
    if (this.wouldCrowd(ctx, nx, nz, heading)) {
      // Deadlock breaker: beside a robot that has been standing still, creep
      // past at walking pace without ever touching it.
      const robotStill = Math.abs(ctx.robot.speed) < CREEP_ROBOT_STILL;
      this.haltedS += ctx.dt;
      if (robotStill && this.haltedS >= CREEP_AFTER_S) {
        // Creep around the robot: try the desired direction first, then
        // increasingly sideways, the side AWAY from the robot first.
        const toRobot = Math.atan2(ctx.robot.x - s.x, ctx.robot.z - s.z);
        const away = wrapAngle(toRobot - heading) > 0 ? -1 : 1;
        step = Math.min(dist, CREEP_SPEED * ctx.dt);
        const backOff = (): boolean => {
          const bx = s.x - Math.sin(s.heading) * step;
          const bz = s.z - Math.cos(s.heading) * step;
          if (this.wouldTouch(ctx, bx, bz, s.heading) || ctx.world.network.clearance(bx, bz) < s.radius) return false;
          s.x = bx;
          s.z = bz;
          this.setVel(-Math.sin(s.heading) * CREEP_SPEED, -Math.cos(s.heading) * CREEP_SPEED);
          return true;
        };
        if (ctx.t < this.reverseUntil && backOff()) return true;
        for (const off of [0, 0.5, 1.0, 1.5]) {
          for (const sign of off === 0 ? [1] : [away, -away]) {
            const h = heading + sign * off;
            const cx = s.x + Math.sin(h) * step;
            const cz = s.z + Math.cos(h) * step;
            if (this.wouldTouch(ctx, cx, cz, h)) continue;
            if (ctx.world.network.clearance(cx, cz) < s.radius) continue;
            s.x = cx;
            s.z = cz;
            this.setVel(Math.sin(h) * CREEP_SPEED, Math.cos(h) * CREEP_SPEED);
            s.heading = h;
            return true;
          }
        }
        // Last resort: back off along the body axis for a committed moment (a
        // rider wedged against a halted robot rolls back ~0.4 m) — never
        // rotating, never touching.
        this.reverseUntil = ctx.t + 0.7;
        if (backOff()) return true;
      }
      // Courtesy halt. Abrupt only when it had real speed to lose, at most
      // once per 2 s (a guard flickering beside a slow robot is ONE event).
      if (this.abruptEligible && this.prevSpeed > 0.4 && this.nowT - this.lastAbruptT >= 2.0) {
        this.abrupt = true;
        this.lastAbruptT = this.nowT;
      }
      this.setVel(0, 0);
      return false;
    }
    this.haltedS = 0;
    const prevX = s.x;
    const prevZ = s.z;
    s.x = nx;
    s.z = nz;
    this.setVel((s.x - prevX) / ctx.dt, (s.z - prevZ) / ctx.dt);
    if (step > 1e-5) s.heading = heading;
    return true;
  }

  protected setVel(vx: number, vz: number): void {
    const s = this.state;
    s.vx = vx;
    s.vz = vz;
    s.safetyVx = vx;
    s.safetyVz = vz;
    const sp = Math.hypot(vx, vz);
    s.moving = sp > 0.05;
    this.prevSpeed = sp;
  }

  protected deactivate(): void {
    const s = this.state;
    s.active = false;
    s.collidable = false;
    this.setVel(0, 0);
  }

  /**
   * Despawn only while unobserved. A body that reaches its path end ON the
   * carriageway with the robot nearby used to pop out of existence in plain
   * sight (the map audit counted thirty of them); now it stands in place —
   * the renderer shows a resting pose — and leaves once it is off the road
   * or the robot is far enough that nobody sees it go.
   */
  protected lingerThenDeactivate(ctx: AgentCtx): void {
    const s = this.state;
    const onRoad = ctx.world.network.sdf(s.x, s.z) < -0.25;
    const near = Math.hypot(ctx.robot.x - s.x, ctx.robot.z - s.z) < 25;
    if (onRoad && near) {
      this.setVel(0, 0);
      return;
    }
    this.deactivate();
  }
}

// ---------------------------------------------------------------------------
// Waypoint follower (shared by vehicles, walkers, the cart).

class WaypointCursor {
  readonly pts: readonly Vec2[];
  index = 1;

  constructor(pts: Vec2[]) {
    if (pts.length < 2) throw new Error("follower needs >= 2 waypoints");
    this.pts = pts;
  }

  get done(): boolean {
    return this.index >= this.pts.length;
  }

  /** Advance past waypoints within `reach` of (x, z) or already behind the
   * waypoint's plane (a lane-shifted body never "reaches" the centre point);
   * return the current target. */
  target(x: number, z: number, reach = 0.6): Vec2 | null {
    while (!this.done) {
      const p = this.pts[this.index]!;
      const a = this.pts[this.index - 1]!;
      const segLen = Math.hypot(p.x - a.x, p.z - a.z) || 1;
      const ux = (p.x - a.x) / segLen;
      const uz = (p.z - a.z) / segLen;
      const passed = (x - p.x) * ux + (z - p.z) * uz >= 0;
      if (Math.hypot(p.x - x, p.z - z) <= reach || passed) this.index++;
      else return p;
    }
    return null;
  }

  /** Direction of travel at the current target (for lane-offset normals). */
  direction(): { fx: number; fz: number } {
    const i = Math.min(this.index, this.pts.length - 1);
    const a = this.pts[i - 1]!;
    const b = this.pts[i]!;
    const len = Math.hypot(b.x - a.x, b.z - a.z) || 1;
    return { fx: (b.x - a.x) / len, fz: (b.z - a.z) / len };
  }

  /** Projection of (x, z) onto the current segment's infinite line — the
   * lane anchor for lateral offsets. Offsetting from the body's OWN position
   * instead let the offset accumulate: a long pass crabbed sideways off the
   * road entirely. */
  linePoint(x: number, z: number): Vec2 {
    const i = Math.min(this.index, this.pts.length - 1);
    const a = this.pts[i - 1]!;
    const b = this.pts[i]!;
    const len = Math.hypot(b.x - a.x, b.z - a.z) || 1;
    const ux = (b.x - a.x) / len;
    const uz = (b.z - a.z) / len;
    const u = (x - a.x) * ux + (z - a.z) * uz;
    return { x: a.x + ux * u, z: a.z + uz * u };
  }
}

// ---------------------------------------------------------------------------
// Research pedestrian (mission port of the lab pedestrian: identical protocol,
// but positions are expressed in the delivery path frame (s, d)).

const PED_LATERAL_SPEED = 0.6;
const PED_WALK = 1.2;

export class ResearchPedAgent extends BaseAgent {
  private readonly swayPhase: number;
  private readonly driftTarget: number;
  private readonly mirrorSign: 1 | -1;
  private readonly path: DeliveryPath;
  private walking = false;
  private baseOff = 0;
  private off = 0;
  /** Own path progress (walks toward decreasing s). */
  s: number;

  constructor(
    id: string,
    spawnS: number,
    variant: "A" | "B" | "C",
    mirrorSign: 1 | -1,
    seed: number,
    path: DeliveryPath,
    walkTriggerS: number,
  ) {
    const p = path.point(spawnS);
    super(
      baseState(id, "research_ped", "research", p.x, p.z, 0.26, wrapAngle(p.theta + Math.PI)),
      walkTriggerS,
    );
    this.path = path;
    this.s = spawnS;
    const rng = new Rng(deriveSeed(seed, 21));
    this.swayPhase = rng.next() * Math.PI * 2;
    this.mirrorSign = mirrorSign;
    const logical = variant === "A" ? -DRIFT_TARGET : variant === "B" ? DRIFT_TARGET : 0;
    this.driftTarget = logical * mirrorSign;
  }

  driftSide(): Side | null {
    if (this.driftTarget === 0) return null;
    return this.driftTarget < 0 ? "left" : "right";
  }

  /** Current lateral offset from the delivery path (m, +right). */
  get lateral(): number {
    return this.off;
  }

  step(ctx: AgentCtx): void {
    const s = this.state;
    if (!s.active) return;
    s.cuePhase = ctx.reactive ? 3 : ctx.cueT !== null ? 2 : 1;
    const prevX = s.x;
    const prevZ = s.z;
    const prevBase = this.baseOff;
    const robotS = ctx.robotFrame.s;
    const robotOff = ctx.robotFrame.d;
    const passed = this.s < robotS - 1.0;

    if (!this.walking && robotS >= (this.triggerS ?? -Infinity)) this.walking = true;

    let baseTarget: number;
    let standing = !this.walking;
    if (ctx.reactive && !passed) {
      const side = this.off >= robotOff ? 1 : -1;
      const lim = this.path.point(this.s).halfWidth - s.radius;
      baseTarget = clamp(robotOff + side * REACTIVE_CLEARANCE, -lim, lim);
      const need = ctx.robotShape.r + s.radius + 0.1;
      if (Math.abs(baseTarget - robotOff) < need) standing = true;
    } else if (ctx.cueT !== null) {
      baseTarget = this.driftTarget * smoothstep01((ctx.t - ctx.cueT) / DRIFT_RAMP_S);
    } else {
      baseTarget = 0;
    }
    const sway =
      this.mirrorSign * SWAY_AMPLITUDE * Math.sin(2 * Math.PI * SWAY_HZ * ctx.t + this.swayPhase);
    const target = baseTarget + sway;

    let speed = PED_WALK;
    if (standing) {
      speed = 0;
    } else if (ctx.reactive && !passed) {
      const rvx = ctx.robot.speed * Math.sin(ctx.robot.heading) - s.vx;
      const rvz = ctx.robot.speed * Math.cos(ctx.robot.heading) - s.vz;
      const ttc = timeToCollision(
        ctx.robot.x - s.x,
        ctx.robot.z - s.z,
        rvx,
        rvz,
        ctx.robotShape.effR + s.radius + SAFETY_MARGIN,
      );
      if (ttc < PED_REACTIVE_TTC) speed = PED_REACTIVE_SLOW_SPEED;
    }

    // Courtesy-halt backstop (never touches the robot): last-resort only. The
    // guard radius sits below the reactive side-pass clearance (0.72) or the
    // pedestrian could never squeeze past a yielded robot.
    if (speed > 0 && !passed) {
      const np = this.path.offsetPoint(this.s - speed * ctx.dt, this.off);
      const guard = s.radius + ctx.robotShape.r + 0.06;
      if (Math.hypot(np.x - ctx.robot.x, np.z - ctx.robot.z) < guard) {
        speed = 0;
        standing = true;
      }
    }

    const prevCenter = this.path.point(this.s);
    this.baseOff += clamp(baseTarget - this.baseOff, -PED_LATERAL_SPEED * ctx.dt, PED_LATERAL_SPEED * ctx.dt);
    this.off += clamp(target - this.off, -PED_LATERAL_SPEED * ctx.dt, PED_LATERAL_SPEED * ctx.dt);
    this.s -= speed * ctx.dt;
    const p = this.path.offsetPoint(this.s, this.off);
    const newCenter = this.path.point(this.s);
    s.x = p.x;
    s.z = p.z;
    s.vx = (s.x - prevX) / ctx.dt;
    s.vz = (s.z - prevZ) / ctx.dt;
    // Sway-free intent velocity: centerline walk + base lateral chase.
    const rx = Math.cos(newCenter.theta);
    const rz = -Math.sin(newCenter.theta);
    s.safetyVx = (newCenter.x - prevCenter.x) / ctx.dt + (rx * (this.baseOff - prevBase)) / ctx.dt;
    s.safetyVz = (newCenter.z - prevCenter.z) / ctx.dt + (rz * (this.baseOff - prevBase)) / ctx.dt;
    s.moving = speed > 0.05;
    if (speed > 0.05) s.heading = wrapAngle(newCenter.theta + Math.PI);

    if (this.s < robotS - 8) this.deactivate();
  }
}

// ---------------------------------------------------------------------------
// Vehicles: lane-following with a destination. Brake for the robot ahead in
// the lane (smooth, TTC-style following), optionally overtake on the left,
// never cut across the robot. Used for motorbikes, motorcycles, cars, and
// the slow vendor cart.

export interface VehicleOptions {
  id: string;
  type: "motorbike" | "motorcycle" | "car" | "truck" | "bus" | "vendor_cart";
  role: string;
  /** Lane-center waypoints (world coords); the vehicle despawns at the last one. */
  waypoints: Vec2[];
  cruise: number;
  /** Start moving when the robot's path progress reaches this (null = immediately). */
  triggerS: number | null;
  /** Readable cue duration before the first movement (s). */
  cueDelayS?: number;
  /** Hold the start while the robot is within this distance BEHIND and
   * approaching — never pull out into a closing robot (pull-out fairness). */
  holdIfRobotBehindM?: number;
  canOvertake?: boolean;
  /** Back along the authored path, facing opposite travel (ADR-080). */
  reverse?: boolean;
  /** Stay where the route ends (visible, collidable) instead of despawning. */
  parkAtEnd?: boolean;
  /** When given, a triggered vehicle idles kerbside and pulls out on cue. */
  network?: RoadNetwork;
  radius?: number;
  body?: number[];
  accel?: number;
  decel?: number;
}

const VEHICLE_DEFAULTS: Record<VehicleOptions["type"], { radius: number; body: number[]; accel: number; decel: number }> = {
  motorbike: { radius: 0.42, body: [0], accel: 3.0, decel: 5.0 },
  motorcycle: { radius: 0.5, body: [-0.35, 0.35], accel: 3.5, decel: 6.0 },
  car: { radius: 0.9, body: [-1.4, 0, 1.4], accel: 2.0, decel: 5.0 },
  // A rigid truck: a cab plus a box body, between a car and a bus.
  truck: { radius: 1.1, body: [-2.2, 0, 2.2], accel: 1.2, decel: 4.0 },
  // A city bus: ~10 m of body on four circles, and the sluggish accel/decel
  // that goes with the mass. The length is the point — it is what makes a bus
  // hide an alley mouth, which is the whole premise of the occlusion maps.
  bus: { radius: 1.25, body: [-3.9, -1.3, 1.3, 3.9], accel: 0.9, decel: 3.2 },
  vendor_cart: { radius: 0.45, body: [0], accel: 0.8, decel: 1.5 },
};

const FOLLOW_GAP = 2.6;
const FOLLOW_LOOK = 30;
const OVERTAKE_SHIFT = 1.6;
const OVERTAKE_BLOCKED_S = 0.8;

/**
 * A vehicle that waits for its trigger does the waiting AT THE KERB, like a
 * parked vehicle, and pulls out on cue — a car standing dead in the middle
 * of the lane that springs to life exactly as the robot approaches reads as
 * staged, which play-testing called out. Returns the waypoints with a
 * kerbside start prepended (preferring the vehicle's own right side), or
 * unchanged when there is no trigger, the spawn is already near a kerb, or
 * no kerb is reachable within a few metres.
 */
export function kerbsideIdleStart(
  waypoints: Vec2[],
  radius: number,
  triggerS: number | null,
  network: RoadNetwork,
): Vec2[] {
  if (triggerS === null || waypoints.length < 2) return waypoints;
  const p0 = waypoints[0]!;
  if (network.clearance(p0.x, p0.z) <= radius + 0.9) return waypoints;
  const p1 = waypoints[1]!;
  const len = Math.hypot(p1.x - p0.x, p1.z - p0.z) || 1;
  const fx = (p1.x - p0.x) / len;
  const fz = (p1.z - p0.z) / len;

  // Right of travel first — vehicles park with the traffic direction. Only
  // a spot SNUG at a kerb counts: a loose "closer than the spawn" fallback
  // parked vehicles half-way across the road, which is the very look this
  // exists to remove.
  const kerbBeside = (o: Vec2): Vec2 | null => {
    for (const side of [
      { x: fz, z: -fx },
      { x: -fz, z: fx },
    ]) {
      for (let t = 0.3; t <= 6.0; t += 0.3) {
        // Park a little BEHIND as well as beside: the pull-out is then a
        // forward diagonal (~53° to the road), the way a vehicle actually
        // leaves a kerb — and within the lane-following realism gate.
        const back = 0.75 * t;
        const q = {
          x: o.x + side.x * t - fx * back,
          z: o.z + side.z * t - fz * back,
        };
        const c = network.clearance(q.x, q.z);
        if (c < radius + 0.05) break; // next step would leave the road
        if (c <= radius + 0.3) return q; // snug at the kerb
      }
    }
    return null;
  };

  const atSpawn = kerbBeside(p0);
  if (atSpawn) return [atSpawn, ...waypoints];
  // No kerb within reach (a junction-mouth spawn): leave it be. Auto-marching
  // such spawns along their route was tried both ways and broke hand-tuned
  // choreography (the district's staged yields and lane-cross rules) — those
  // few actors are staged individually in their maps instead.
  return waypoints;
}

export class VehicleAgent extends BaseAgent {
  private readonly cursor: WaypointCursor;
  private readonly cruise: number;
  private readonly accel: number;
  private readonly decel: number;
  private readonly cueDelayS: number;
  private readonly holdBehindM: number;
  private readonly canOvertake: boolean;
  private readonly parkAtEnd: boolean;
  private readonly reverse: boolean;
  private lastReverseHaltT = -Infinity;
  private speed = 0;
  private cueStart: number | null = null;
  private started = false;
  private lateral = 0;
  private lateralTarget = 0;
  private blockedS = 0;
  private overtaking = false;
  /** Seconds stopped behind a robot that is itself standing still. */
  private stalledS = 0;
  /** Latched creep-past mode (cleared once the robot moves or is clear). */
  private creeping = false;

  constructor(o: VehicleOptions) {
    const d = VEHICLE_DEFAULTS[o.type];
    const wps = o.network && !o.reverse
      ? kerbsideIdleStart(o.waypoints, o.radius ?? d.radius, o.triggerS, o.network)
      : o.waypoints;
    const p0 = wps[0]!;
    const p1 = wps[1]!;
    super(
      baseState(
        o.id,
        o.type,
        o.role,
        p0.x,
        p0.z,
        o.radius ?? d.radius,
        o.reverse
          ? wrapAngle(Math.atan2(p1.x - p0.x, p1.z - p0.z) + Math.PI)
          : Math.atan2(p1.x - p0.x, p1.z - p0.z),
        o.body ?? d.body,
      ),
      o.triggerS,
    );
    this.cursor = new WaypointCursor(wps);
    this.reverse = o.reverse ?? false;
    this.cruise = this.reverse ? clamp(o.cruise, 0, 1.5) : o.cruise;
    this.accel = o.accel ?? d.accel;
    this.decel = o.decel ?? d.decel;
    this.cueDelayS = this.reverse ? Math.max(0.8, o.cueDelayS ?? 0) : o.cueDelayS ?? 0;
    this.holdBehindM = o.holdIfRobotBehindM ?? 0;
    this.canOvertake = o.canOvertake ?? false;
    this.parkAtEnd = this.reverse || (o.parkAtEnd ?? false);
  }

  private arrive(ctx: AgentCtx): void {
    if (this.parkAtEnd) {
      this.state.cuePhase = 0;
      this.setVel(0, 0);
      this.speed = 0;
    } else this.lingerThenDeactivate(ctx);
  }

  /**
   * True when (px, pz) is a place this vehicle could actually drive: on the
   * carriageway, not inside a prop, not on top of another road user. The
   * go-around side probes use it (ADR-066) — network.clearance alone let a
   * rider commit a pass straight into a parked row or an oncoming bus.
   */
  private sideClear(ctx: AgentCtx, px: number, pz: number): boolean {
    const r = this.state.radius;
    if (ctx.world.network.clearance(px, pz) <= r + 0.2) return false;
    for (const ob of ctx.obstacles ?? []) {
      if (Math.abs(px - ob.x) < ob.halfW + r + 0.3 && Math.abs(pz - ob.z) < ob.halfD + r + 0.3) {
        return false;
      }
    }
    for (const o of ctx.others ?? []) {
      if (o.id === this.state.id || !o.active || !o.collidable) continue;
      if (Math.hypot(px - o.x, pz - o.z) < r + o.radius + 0.4) return false;
    }
    return true;
  }

  /** Minimum full-body clearance at a pose; positive means a legal backing pose. */
  private reverseClearance(ctx: AgentCtx, x: number, z: number, heading: number): number {
    const s = this.state;
    let gap = Infinity;
    const robots = bodyCircles(ctx.robot, ctx.robotShape);
    for (const offset of s.body) {
      const cx = x + Math.sin(heading) * offset;
      const cz = z + Math.cos(heading) * offset;
      gap = Math.min(gap, ctx.world.network.clearance(cx, cz) - s.radius - 0.05);
      for (const ob of ctx.obstacles ?? []) {
        const dx = Math.max(0, Math.abs(cx - ob.x) - ob.halfW);
        const dz = Math.max(0, Math.abs(cz - ob.z) - ob.halfD);
        gap = Math.min(gap, Math.hypot(dx, dz) - s.radius - 0.12);
      }
      for (const c of robots) {
        gap = Math.min(gap, Math.hypot(cx - c.x, cz - c.z) - s.radius - ctx.robotShape.r - GUARD_MARGIN);
      }
      for (const other of ctx.others ?? []) {
        if (other.id === s.id || !other.active || !other.collidable) continue;
        for (const c of agentCircles(other)) {
          gap = Math.min(gap, Math.hypot(cx - c.x, cz - c.z) - s.radius - other.radius - GUARD_MARGIN);
        }
      }
    }
    return gap;
  }

  /** Sweep translation AND rotation. Sampling margin bounds each body's travel
   * between samples, so even a thin prop cannot be skipped by a long timestep. */
  private reverseSweepClear(ctx: AgentCtx, x: number, z: number, heading: number): boolean {
    const s = this.state;
    const angle = wrapAngle(heading - s.heading);
    const bound = Math.hypot(x - s.x, z - s.z) + Math.max(...s.body.map(Math.abs)) * Math.abs(angle);
    const count = Math.max(1, Math.ceil(bound / 0.04));
    const margin = bound / count / 2;
    for (let i = 0; i <= count; i++) {
      const u = i / count;
      if (this.reverseClearance(ctx, s.x + (x - s.x) * u, s.z + (z - s.z) * u,
        s.heading + angle * u) < margin) return false;
    }
    return true;
  }

  /** Backing has its own guard: brake and wait on the authored route until clear.
   * It must never enter the forward follower's detour or deadlock-creep logic. */
  private stepReverse(ctx: AgentCtx, target: Vec2): void {
    const s = this.state;
    const dx = target.x - s.x;
    const dz = target.z - s.z;
    const distance = Math.hypot(dx, dz);
    const ux = dx / distance;
    const uz = dz / distance;
    const heading = wrapAngle(Math.atan2(ux, uz) + Math.PI);
    // Stop at every authored waypoint, allowing safe changes of backing direction.
    // Dense, gentle paths should be authored for a curved parking manoeuvre.
    const decel = Math.max(0.01, this.decel);
    const look = Math.min(distance, this.cruise * this.cruise / (2 * decel) + this.cruise * ctx.dt + 0.4);
    let room = look;
    const probes = Math.max(1, Math.ceil(look / 0.04));
    for (let i = 0; i <= probes; i++) {
      const travel = look * i / probes;
      if (this.reverseClearance(ctx, s.x + ux * travel, s.z + uz * travel, heading) < 0.04) {
        room = Math.max(0, travel - 0.08);
        break;
      }
    }
    // Discrete-step stopping law reserves this step's travel before braking.
    const brakeSpeed = (gap: number): number =>
      Math.max(0, Math.sqrt((decel * ctx.dt) ** 2 + 2 * decel * gap) - decel * ctx.dt);
    const targetSpeed = Math.min(this.cruise, brakeSpeed(distance), brakeSpeed(room));
    this.speed = Math.max(0, this.speed + clamp(targetSpeed - this.speed, -decel * ctx.dt, this.accel * ctx.dt));
    // Finish the last submillimetre without overshoot or an asymptotic parking tail.
    const travel = Math.min(distance, this.speed * ctx.dt);
    const nx = s.x + ux * travel;
    const nz = s.z + uz * travel;
    if (!this.reverseSweepClear(ctx, nx, nz, heading)) {
      if (this.prevSpeed > 0.4 && ctx.t - this.lastReverseHaltT >= 2) {
        this.abrupt = true;
        this.lastReverseHaltT = ctx.t;
      }
      this.speed = 0;
      this.setVel(0, 0);
      s.cuePhase = 2;
      return;
    }
    if (travel > 0) {
      s.x = nx;
      s.z = nz;
      s.heading = heading;
    }
    this.setVel(ux * travel / ctx.dt, uz * travel / ctx.dt);
    s.cuePhase = this.speed > 0.01 ? 3 : 2;
    if (!this.cursor.target(s.x, s.z, 1e-6)) this.arrive(ctx);
  }

  /** Robot position in this vehicle's frame: ahead (+forward) and lateral (+right). */
  private robotRel(ctx: AgentCtx): { ahead: number; lat: number; closing: number } {
    const s = this.state;
    const fx = Math.sin(s.heading);
    const fz = Math.cos(s.heading);
    const dx = ctx.robot.x - s.x;
    const dz = ctx.robot.z - s.z;
    const ahead = dx * fx + dz * fz;
    const lat = dx * fz - dz * fx;
    const robotAlong = ctx.robot.speed * (Math.sin(ctx.robot.heading) * fx + Math.cos(ctx.robot.heading) * fz);
    return { ahead, lat, closing: this.speed - robotAlong };
  }

  step(ctx: AgentCtx): void {
    const s = this.state;
    if (!s.active) return;

    if (!this.started) {
      if (this.cueStart === null) {
        if (this.triggerS === null || ctx.robotFrame.s >= this.triggerS) this.cueStart = ctx.t;
        this.setVel(0, 0);
        if (this.cueStart === null) return;
      }
      const u = ctx.t - this.cueStart;
      s.cuePhase = this.cueDelayS > 0 ? (u < this.cueDelayS * 0.5 ? 1 : 2) : 2;
      if (u < this.cueDelayS) {
        this.setVel(0, 0);
        return;
      }
      // Pull-out fairness: never start into the face of a closing robot
      // behind us; wait until it has passed or is comfortably far.
      if (!this.reverse && this.holdBehindM > 0) {
        const rel = this.robotRel(ctx);
        const closingBehind = rel.ahead < 0.5 && -rel.ahead < this.holdBehindM && ctx.robot.speed > 0.3;
        if (closingBehind) {
          s.cuePhase = 2; // holds, indicating
          this.setVel(0, 0);
          return;
        }
      }
      this.started = true;
    }
    s.cuePhase = Math.max(s.cuePhase, 3);

    if (this.cursor.done) {
      this.arrive(ctx);
      return;
    }
    const target = this.cursor.target(s.x, s.z, this.reverse ? 1e-6 : Math.max(0.6, this.speed * ctx.dt * 2));
    if (!target) {
      this.arrive(ctx);
      return;
    }

    if (this.reverse) {
      this.stepReverse(ctx, target);
      return;
    }

    // --- Speed: cruise, but follow the robot when it is ahead in our lane. ---
    let targetSpeed = this.cruise;
    s.cuePhase = 3;
    const rel = this.robotRel(ctx);
    const front = Math.max(...s.body) + s.radius;
    const corridor = s.radius + ctx.robotShape.effR + 0.35;
    const inLaneAhead = rel.ahead > 0 && rel.ahead < FOLLOW_LOOK && Math.abs(rel.lat) < corridor;
    if (inLaneAhead) {
      const gap = rel.ahead - front - ctx.robotShape.effR;
      const robotAlong = Math.max(0, this.speed - rel.closing);
      // Speed from which this vehicle can still brake to the follow gap
      // (v^2 = v_lead^2 + 2 a d, with a safety fraction of its own decel):
      // smooth, physically consistent following; never a guard-zone dive.
      const room = Math.max(0, gap - FOLLOW_GAP);
      targetSpeed = clamp(Math.sqrt(robotAlong * robotAlong + 2 * this.decel * 0.7 * room), 0, this.cruise);
      // Readable warning (horn / bell) while closing on the robot from behind.
      if (rel.closing > 0.5 && rel.ahead < 14) s.cuePhase = 4;
    }

    // --- Going around: a blocked vehicle steers past and keeps going where
    //     the road allows (never inside the roundabout: circulating traffic
    //     keeps its lane). canOvertake vehicles keep the left-shift overtake;
    //     TWO-WHEELERS always may, on either side — a rider does not park
    //     behind a robot standing in (or coming down) their lane, they slow,
    //     pick the side with room, and go (ADR-066).
    const twoWheeler = s.type === "motorbike" || s.type === "motorcycle";
    const onRing = ctx.world.network.pieces[ctx.world.network.deepestPiece(s.x, s.z)]?.kind === "ring";
    if ((this.canOvertake || twoWheeler) && !onRing) {
      const blocked = inLaneAhead && targetSpeed < this.cruise * 0.6;
      this.blockedS = blocked ? this.blockedS + ctx.dt : 0;
      // A rider commits quickly — braking toward a standstill first and only
      // then deciding is exactly the frozen-mid-road look this replaces.
      const commitS = twoWheeler ? OVERTAKE_BLOCKED_S * 0.5 : OVERTAKE_BLOCKED_S;
      if (!this.overtaking && this.blockedS >= commitS) {
        const dir = this.cursor.direction();
        const lx = dir.fz; // right normal of (fx, fz) is (fz, -fx)
        const lz = -dir.fx;
        // Overtakers pass on the left; a rider prefers whichever side the
        // robot is NOT on, and takes the other ONLY if that one truly has
        // room — past the curbs, past the props, past other road users, and
        // past the robot itself. A side whose line would run through the
        // robot is no pass at all (it would just trade the follow-stop for a
        // guard-stop beside it), so with no clear side the rider stays put:
        // some streets really are blocked, and standing in one of those is
        // the realistic move.
        const sides = twoWheeler ? (rel.lat >= 0 ? [-1, 1] : [1, -1]) : [-1];
        for (const side of sides) {
          if (Math.abs(rel.lat - side * OVERTAKE_SHIFT) < corridor + 0.15) continue;
          const probeX = s.x + lx * side * OVERTAKE_SHIFT;
          const probeZ = s.z + lz * side * OVERTAKE_SHIFT;
          if (!this.sideClear(ctx, probeX, probeZ)) continue;
          this.overtaking = true;
          this.lateralTarget = side * OVERTAKE_SHIFT;
          break;
        }
      }
      // While going around, a RIDER keeps rolling: ease past at a careful
      // pace rather than obeying the follow law's full stop — but only while
      // the gap is still comfortable, so the courtesy guard is a backstop,
      // never the brake (a guard halt from pace is an abrupt event the
      // research metric counts).
      if (this.overtaking && twoWheeler && inLaneAhead && rel.ahead > front + ctx.robotShape.effR + 1.2) {
        targetSpeed = Math.max(targetSpeed, Math.min(this.cruise, 1.6));
      }
      // Roads curve and traffic moves: the commit probe cannot see either,
      // so re-probe the shifted line every step and fall back into lane the
      // moment it stops being clear — a rider never leaves the carriageway
      // to finish a pass.
      if (this.overtaking) {
        const dir = this.cursor.direction();
        const px = s.x + dir.fx * 1.5 + dir.fz * this.lateralTarget;
        const pz = s.z + dir.fz * 1.5 - dir.fx * this.lateralTarget;
        if (ctx.world.network.clearance(px, pz) < s.radius + 0.1) {
          this.overtaking = false;
          this.lateralTarget = 0;
        }
      }
      if (this.overtaking && rel.ahead < -(front + ctx.robotShape.effR + 4)) {
        this.overtaking = false;
        this.lateralTarget = 0;
      }
    } else if (this.overtaking) {
      this.overtaking = false;
      this.lateralTarget = 0;
    }
    this.lateral += clamp(this.lateralTarget - this.lateral, -1.5 * ctx.dt, 1.5 * ctx.dt);

    // Stalled behind a robot that is not moving either: after a while, creep
    // past it (guardedMove steers the creep around the robot) until the robot
    // moves again or is clear of our path.
    const robotStill = Math.abs(ctx.robot.speed) < CREEP_ROBOT_STILL;
    const robotDist = Math.hypot(ctx.robot.x - s.x, ctx.robot.z - s.z);
    const followStop = front + ctx.robotShape.effR + FOLLOW_GAP + 1.0;
    const robotNear = robotDist < Math.max(this.guardRadius(ctx) + 1.0, followStop);
    if ((inLaneAhead || robotNear) && robotStill && this.speed < 0.05) this.stalledS += ctx.dt;
    else this.stalledS = 0;
    if (this.stalledS >= CREEP_AFTER_S) this.creeping = true;
    if (this.creeping && (!robotStill || robotDist > followStop + 4)) this.creeping = false;
    if (this.creeping) targetSpeed = CREEP_SPEED;

    this.speed += clamp(targetSpeed - this.speed, -this.decel * ctx.dt, this.accel * ctx.dt);
    this.speed = Math.max(0, this.speed);

    const dir = this.cursor.direction();
    // Aim a few meters ahead along the lane, shifted sideways by the current
    // lateral offset (aiming at a far waypoint would make the shift vanish).
    // The shifted aim is anchored to the LANE LINE, not the body's own
    // position — self-relative aiming compounded into a sideways crab.
    const aheadM = Math.min(4, Math.hypot(target.x - s.x, target.z - s.z));
    const base = this.lateral !== 0 ? this.cursor.linePoint(s.x, s.z) : target;
    const tx = (this.lateral !== 0 ? base.x + dir.fx * aheadM : target.x) + dir.fz * this.lateral;
    const tz = (this.lateral !== 0 ? base.z + dir.fz * aheadM : target.z) - dir.fx * this.lateral;
    if (this.speed > 1e-3) {
      if (this.creeping) this.haltedS = Math.max(this.haltedS, CREEP_AFTER_S);
      this.guardedMove(ctx, tx, tz, this.speed);
    } else this.setVel(0, 0);
    if (this.cursor.done) this.arrive(ctx);
  }
}

// ---------------------------------------------------------------------------
// People: walkers (alone or in a group), standing/talking people.

export class WalkerAgent extends BaseAgent {
  private readonly cursor: WaypointCursor;
  private readonly speed: number;
  private started = false;

  constructor(id: string, role: string, waypoints: Vec2[], speed: number, triggerS: number | null) {
    const p0 = waypoints[0]!;
    const p1 = waypoints[1]!;
    super(
      baseState(id, "walker", role, p0.x, p0.z, 0.26, Math.atan2(p1.x - p0.x, p1.z - p0.z)),
      triggerS,
    );
    this.cursor = new WaypointCursor(waypoints);
    this.speed = speed;
  }

  step(ctx: AgentCtx): void {
    const s = this.state;
    if (!s.active) return;
    if (!this.started) {
      if (this.triggerS !== null && ctx.robotFrame.s < this.triggerS) {
        this.setVel(0, 0);
        return;
      }
      this.started = true;
      s.cuePhase = 1;
    }
    const target = this.cursor.target(s.x, s.z, 0.5);
    if (!target) {
      this.lingerThenDeactivate(ctx);
      return;
    }
    // Pedestrians keep walking; the guard only stops them from touching the robot.
    this.guardedMove(ctx, target.x, target.z, this.speed);
    if (this.cursor.done) this.lingerThenDeactivate(ctx);
  }
}

export class StandingPersonAgent extends BaseAgent {
  private readonly facing: number;

  constructor(id: string, role: string, x: number, z: number, facing: number) {
    super(baseState(id, "standing_person", role, x, z, 0.26, facing), null);
    this.facing = facing;
    this.abruptEligible = false;
  }

  step(ctx: AgentCtx): void {
    const s = this.state;
    // Talking animation beat for the renderer; never moves.
    s.cuePhase = 1 + (Math.floor(ctx.t * 1.3) % 2);
    s.heading = this.facing;
    this.setVel(0, 0);
  }
}

// ---------------------------------------------------------------------------
// Cat: sits at the curb, crouches (readable), then dashes across the ALLEY —
// but cats wait for a closing robot and dash behind it rather than under it.

export class CatAgent extends BaseAgent {
  private readonly from: Vec2;
  private readonly to: Vec2;
  private readonly crossS: number;
  private cueStart: number | null = null;
  private phase: "sit" | "crouch" | "dash" | "hide" = "sit";
  private hideT = 0;

  constructor(id: string, from: Vec2, to: Vec2, crossS: number, triggerS: number) {
    super(
      baseState(id, "cat", "crossing", from.x, from.z, 0.16, Math.atan2(to.x - from.x, to.z - from.z)),
      triggerS,
    );
    this.from = from;
    this.to = to;
    this.crossS = crossS;
    this.abruptEligible = false;
  }

  step(ctx: AgentCtx): void {
    const s = this.state;
    if (!s.active) return;
    switch (this.phase) {
      case "sit":
        this.setVel(0, 0);
        if (this.cueStart === null && ctx.robotFrame.s >= (this.triggerS ?? -Infinity)) {
          this.cueStart = ctx.t;
          this.phase = "crouch";
          s.cuePhase = 1;
        }
        return;
      case "crouch": {
        this.setVel(0, 0);
        if (ctx.t - (this.cueStart ?? ctx.t) < 0.8) return;
        // Fairness: dash only if the robot cannot reach the crossing line
        // before the cat is across (or has already passed it).
        const crossTime = Math.hypot(this.to.x - this.from.x, this.to.z - this.from.z) / 3.5;
        const gap = this.crossS - ctx.robotFrame.s;
        const robotEta = gap / Math.max(ctx.robot.speed, 0.1);
        if (gap < -1.0 || robotEta > crossTime + 0.5) {
          this.phase = "dash";
          s.cuePhase = 2;
        }
        return;
      }
      case "dash": {
        this.guardedMove(ctx, this.to.x, this.to.z, 3.5);
        if (Math.hypot(s.x - this.to.x, s.z - this.to.z) < 0.1) {
          this.phase = "hide";
          s.cuePhase = 3;
          this.hideT = ctx.t;
        }
        return;
      }
      case "hide":
        // Curled up under the parked bikes: visible, no longer in play.
        this.setVel(0, 0);
        if (ctx.t - this.hideT > 1.0) s.collidable = false;
    }
  }
}

// ---------------------------------------------------------------------------
// Playful dog: trots toward the delivery box, keeps a safe orbit beside the
// robot (in the robot's own frame), then darts to its home curb once the
// player chooses.

export class DogAgent extends BaseAgent {
  private readonly homeSide: 1 | -1;
  private readonly path: DeliveryPath;
  private phase: "sit" | "approach" | "settle" | "watching" = "sit";
  private cueStart: number | null = null;
  private settleTarget: Vec2 | null = null;

  constructor(id: string, sitPoint: Vec2, homeSide: 1 | -1, triggerS: number, path: DeliveryPath) {
    super(baseState(id, "dog", "playful", sitPoint.x, sitPoint.z, 0.18, 0), triggerS);
    this.homeSide = homeSide;
    this.path = path;
    this.abruptEligible = false;
  }

  step(ctx: AgentCtx): void {
    const s = this.state;
    if (!s.active) return;
    const toRobot = Math.hypot(ctx.robot.x - s.x, ctx.robot.z - s.z);
    const fx = Math.sin(ctx.robot.heading);
    const fz = Math.cos(ctx.robot.heading);
    const rx = fz; // robot's right
    const rz = -fx;
    switch (this.phase) {
      case "sit":
        if (this.cueStart === null && ctx.robotFrame.s >= (this.triggerS ?? -Infinity)) {
          this.cueStart = ctx.t;
          s.cuePhase = 1; // perks up, eyes on the box
        }
        this.setVel(0, 0);
        s.heading = Math.atan2(ctx.robot.x - s.x, ctx.robot.z - s.z);
        if (this.cueStart !== null && ctx.t - this.cueStart >= 1.0) this.phase = "approach";
        return;
      case "approach": {
        s.cuePhase = 2;
        const ORBIT = 0.85;
        const besideX = ctx.robot.x + rx * this.homeSide * 0.8 + fx * 0.9;
        const besideZ = ctx.robot.z + rz * this.homeSide * 0.8 + fz * 0.9;
        if (toRobot > ORBIT + 0.1) {
          this.guardedMove(ctx, besideX, besideZ, 1.6);
        } else if (toRobot < ORBIT - 0.15) {
          const away = Math.atan2(s.x - ctx.robot.x, s.z - ctx.robot.z);
          this.guardedMove(ctx, s.x + Math.sin(away), s.z + Math.cos(away), 1.8);
        } else {
          this.setVel(0, 0);
          s.heading = Math.atan2(ctx.robot.x - s.x, ctx.robot.z - s.z);
        }
        const dogS = this.path.project(s.x, s.z, ctx.robotFrame.s).s;
        if (ctx.decision !== null || ctx.robotFrame.s > dogS + 0.5) {
          this.phase = "settle";
          const aheadS = ctx.robotFrame.s + 3;
          const hw = this.path.point(aheadS).halfWidth;
          this.settleTarget = this.path.offsetPoint(aheadS, this.homeSide * (hw - 0.3));
        }
        return;
      }
      case "settle": {
        s.cuePhase = 3;
        const t = this.settleTarget!;
        this.guardedMove(ctx, t.x, t.z, 2.0);
        if (Math.hypot(s.x - t.x, s.z - t.z) < 0.1) this.phase = "watching";
        return;
      }
      case "watching": {
        s.cuePhase = 4;
        this.setVel(0, 0);
        s.heading = Math.atan2(ctx.robot.x - s.x, ctx.robot.z - s.z);
        // The dog watched the robot go, then popped out of existence 4 m
        // behind it — in plain view. It keeps watching until nobody sees.
        const dogS = this.path.project(s.x, s.z, ctx.robotFrame.s).s;
        if (ctx.robotFrame.s > dogS + 4) this.lingerThenDeactivate(ctx);
      }
    }
  }
}
